import { ApiService } from './apiService';
import { API_ENDPOINTS } from '../config/api';
import { ApiError, PaginatedResponse } from '../types/api';
import {
  NotificationItem,
  NotificationModule,
  NotificationsQuery,
  NotificationTab,
  StaffNotificationType,
} from '../types/notification';
import { appendQueryString } from '../utils/queryParams';

type RawNotification = Record<string, unknown>;

const toStringValue = (value: unknown, fallback = ''): string =>
  value === undefined || value === null ? fallback : String(value);

const normalizeModule = (value: unknown): NotificationModule =>
  value === 'Matches' ? 'Matches' : 'Tasks';

export const normalizeNotification = (raw: RawNotification): NotificationItem => ({
  notificationId: Number(raw.notificationId ?? raw.notification_id ?? 0),
  title: toStringValue(raw.title),
  subtitle: toStringValue(raw.subtitle),
  dateStr: toStringValue(raw.dateStr ?? raw.date_str),
  module: normalizeModule(raw.module),
  isNew: Boolean(raw.isNew ?? raw.is_new ?? false),
  type: toStringValue(raw.type, 'task_assigned') as StaffNotificationType,
  fromUserId:
    raw.fromUserId != null || raw.from_user_id != null
      ? Number(raw.fromUserId ?? raw.from_user_id)
      : null,
  data:
    raw.data && typeof raw.data === 'object'
      ? (raw.data as NotificationItem['data'])
      : {},
});

export const getNotificationErrorMessage = (error: unknown, fallback: string): string => {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as ApiError).message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
};

export class NotificationService extends ApiService {
  async getNotifications(
    params?: NotificationsQuery
  ): Promise<PaginatedResponse<NotificationItem>> {
    const tab: NotificationTab = params?.tab ?? 'all';
    const page = params?.page ?? 1;
    const limit = params?.limit ?? 10;

    const url = appendQueryString(API_ENDPOINTS.notifications.list, { tab, page, limit });
    const response = await this.get<PaginatedResponse<RawNotification>>(url);

    return {
      ...response,
      data: (response.data ?? []).map(normalizeNotification),
    };
  }

  async getUnreadCount(): Promise<number> {
    const response = await this.get<{ count?: number } | number>(
      API_ENDPOINTS.notifications.unreadCount
    );

    if (typeof response === 'number') {
      return response;
    }

    return Number(response.count ?? 0);
  }

  async markAllAsRead(): Promise<void> {
    await this.put<void>(API_ENDPOINTS.notifications.readAll);
  }

  async markAsRead(notificationId: number): Promise<void> {
    await this.put<void>(API_ENDPOINTS.notifications.read(notificationId));
  }

  async deleteNotification(notificationId: number): Promise<void> {
    await this.put<void>(API_ENDPOINTS.notifications.delete(notificationId));
  }
}

export const notificationService = new NotificationService();
